import React, { useCallback, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Checkbox, debounce, IconButton, Stack, TextField, Typography } from "@mui/material";
import { BinDeleteIcon } from "assets/material-custom-icons";
import { SelectAutocomplete } from "components/forms";
import { useDidUpdate, useModals } from "hooks";
import { useDealActions } from "store";
import { IAssetClass } from "store/types";

import { IManageAssetClassesContext, useManageAssetClassesContext } from ".";

interface Props {
  assetClass: IAssetClass;
  hideSystemAssetClass?: boolean;
}

const filterSubAssetByIsHidden = (subAssetClass: IAssetClass[]) => subAssetClass.filter(({ is_hidden }) => !is_hidden);

export const ExistingRowForm: React.FC<Props> = ({ assetClass, hideSystemAssetClass = false }) => {
  const { getDealsFiltersAction } = useDealActions();
  const { sub_asset_class, is_hidden, id, account_id } = assetClass;
  const { deleteModal } = useModals();

  const {
    updateAssetClass,
    updateSubAssetClass,
    setIsHiddenSubAssets,
    deleteSubAsset,
    createSubAsset,
    deleteAssetClass
  } = useManageAssetClassesContext();

  const updateAssetClassWithRevert: IManageAssetClassesContext["updateAssetClass"] = useCallback(
    async (data) => {
      try {
        await updateAssetClass(data);
        getDealsFiltersAction();
      } catch (_error) {
        setName(assetClass.account_alias || assetClass.presentation_name);
      }
    },
    [updateAssetClass, assetClass]
  );

  const updateAssetClassDebounced = useCallback(debounce(updateAssetClassWithRevert, 1000), [
    updateAssetClassWithRevert
  ]);

  const [selectedAssetClasses, setSelectedAssetClasses] = useState<IAssetClass[]>(
    filterSubAssetByIsHidden(sub_asset_class)
  );
  const [name, setName] = useState<string>(assetClass.account_alias || assetClass.presentation_name);

  const updateSubAssetWithRevert: IManageAssetClassesContext["updateSubAssetClass"] = async (data) => {
    try {
      await updateSubAssetClass(data);
    } catch (_error) {
      // Return back state in case API reject the call
      setSelectedAssetClasses(filterSubAssetByIsHidden(sub_asset_class));
    }
  };

  const setIsHiddenSubAssetsWithRevert: IManageAssetClassesContext["setIsHiddenSubAssets"] = async (data) => {
    try {
      await setIsHiddenSubAssets(data);
    } catch (_error) {
      // Return back state in case API reject the call
      setSelectedAssetClasses(filterSubAssetByIsHidden(sub_asset_class));
    }
  };

  const handleDeleteSubAsset: IManageAssetClassesContext["deleteSubAsset"] = async (data) => {
    deleteModal({
      action: async () => {
        try {
          await deleteSubAsset(data);
        } catch (_error) {
          // Return back state in case API reject the call
          setSelectedAssetClasses(filterSubAssetByIsHidden(sub_asset_class));
        }
      }
    });
  };

  const handleDeleteAssetClass: IManageAssetClassesContext["deleteAssetClass"] = async () => {
    deleteModal({
      action: async () => await deleteAssetClass(assetClass.id)
    });
  };
  useDidUpdate(() => {
    setSelectedAssetClasses(filterSubAssetByIsHidden(sub_asset_class));
  }, [sub_asset_class]);

  return (
    <Stack sx={{ flexDirection: "row", p: "8px 0", gap: "12px" }}>
      <TextField
        sx={{
          flex: "20%",
          minWidth: 216,
          "& .MuiInputBase-root": { height: "32px" }
        }}
        value={name}
        {...(!name && {
          helperText: "Name is required",
          error: true
        })}
        onChange={(e) => {
          setName(e.target.value);
          e.target.value && updateAssetClassDebounced({ id: assetClass.id, name: e.target.value });
        }}
      />
      {!hideSystemAssetClass && (
        <TextField
          disabled
          sx={{
            flex: "20%",
            "& .MuiInputBase-root": { height: "32px" }
          }}
          defaultValue={assetClass.name}
        />
      )}
      <SelectAutocomplete
        showBackdrop
        value={selectedAssetClasses}
        // disabled={!user_id}
        disableClearable
        ListboxProps={{
          sx: {
            maxHeight: "200px"
          }
        }}
        sx={{
          flex: hideSystemAssetClass ? "80%" : "60%",
          "& .MuiInputBase-root": {
            pt: "2px !important",
            pb: "2px !important"
          }
        }}
        multiple
        selectAll
        disableCloseOnSelect
        options={assetClass.sub_asset_class}
        createNew={{
          title: "Add New Sub Asset Class",
          buttonProps: {
            startIcon: <AddIcon />,
            onMouseDown: () => {
              createSubAsset({ assetClassId: assetClass.id });
            }
          }
        }}
        renderOption={(props, option) => {
          const isSelected = Boolean(props["aria-selected"]);
          return (
            <li {...props}>
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  height: 36,
                  width: "100%"
                }}
              >
                <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
                  <Checkbox
                    disabled
                    checked={isSelected}
                    sx={{
                      "&.Mui-checked": {
                        color: "#071F35",
                        "&.Mui-disabled": {
                          color: "#071F35"
                        }
                      },
                      "&.MuiCheckbox-root": {
                        color: "#071F35",
                        "&.Mui-disabled": {
                          color: "#071F35"
                        }
                      }
                    }}
                  />
                  <Typography>{option.name}</Typography>
                  {option.account_id && <PersonOutlineIcon />}
                </Stack>
                {option.account_id && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSubAsset({ assetClassId: assetClass.id, subAssetClassId: option.id });
                    }}
                    size="sm"
                  >
                    <BinDeleteIcon />
                  </IconButton>
                )}
              </Stack>
            </li>
          );
        }}
        getOptionLabel={(option) => option.name}
        compareParam={(option) => option.name}
        setValue={(value, reason, details) => {
          const { option } = details || {};

          // Handle each option separately
          if (reason === "selectOption" && option) {
            updateSubAssetWithRevert({
              assetClassId: assetClass.id,
              subAssetClassId: option.id,
              is_hidden: false
            });
          }

          if (reason === "removeOption" && option) {
            updateSubAssetWithRevert({
              assetClassId: assetClass.id,
              subAssetClassId: option.id,
              is_hidden: true
            });
          }

          if (reason === "selectAll") {
            setIsHiddenSubAssetsWithRevert({
              assetClassId: assetClass.id,
              is_hidden: false
            });
          }

          if (reason === "unselectAll") {
            setIsHiddenSubAssetsWithRevert({
              assetClassId: assetClass.id,
              is_hidden: true
            });
          }

          // Set local state anyway. It will be revert back in case API reject the call
          setSelectedAssetClasses(value);
        }}
        renderInput={(params) => <TextField {...params} placeholder="Select Sub Asset Classes" />}
        onChange={() => {}}
      />
      <IconButton
        onClick={() => {
          updateAssetClass({ id: assetClass.id, is_hidden: !is_hidden });
        }}
        sx={{ color: "#92A7BC" }}
      >
        {is_hidden ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
      </IconButton>
      {account_id && (
        <IconButton onClick={() => handleDeleteAssetClass(id)}>
          <BinDeleteIcon />
        </IconButton>
      )}
    </Stack>
  );
};
